// GZ#2268 / GZ#1821: react-beautiful-dnd throws its own `RbdInvariant`, a
// plain `{ message }` object (not an Error), and in production the message is
// always "Invariant failed". Its window error handler catches it, aborts the
// drag and carries on, so the admin sees nothing. Sentry's wrapped listener
// captures it first, as "Object captured as exception with keys: message".
// Drop only that exact shape; a real Error("Invariant failed") still reports.
type SentryLikeEvent = {
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      stacktrace?: { frames?: Array<{ filename?: string; lineno?: number }> };
    }>;
  };
  extra?: Record<string, unknown>;
} | null | undefined;

export function isRecoveredDndInvariant(event: SentryLikeEvent): boolean {
  if (!event) return false;
  const value = event.exception?.values?.[0]?.value || "";
  if (!value.startsWith("Object captured as exception with keys: message")) return false;
  const serialized = event.extra?.__serialized__ as Record<string, unknown> | undefined;
  if (!serialized || typeof serialized !== "object") return false;
  const keys = Object.keys(serialized);
  return keys.length === 1 && serialized.message === "Invariant failed";
}

// GZ#4380: after a deploy, an open tab asks for an old hashed chunk, hosting
// answers with index.html and the parse fails with "Unexpected token '<'" in
// that chunk. index.tsx already reloads the tab once (GZ#2754), but Sentry
// captures the error first and each deploy added new groups to the board.
// Drop only that exact shape: the exact message, thrown in a hashed chunk file.
// A JSON parse of an HTML API answer has a longer message and still reports.
// Chrome/Safari wording, then Firefox wording for the same failure.
//
// GZ#XXXX (Jungle Padel): after firebase.json fix, missing chunks return 404
// instead of HTML. But a chunk that still parses as HTML after a cache-busting
// retry indicates a deeper problem (e.g., CDN poisoning not cleared by ?v=), so
// we still filter the auto-recovered case while letting persistent failures through.
const STALE_CHUNK_PARSE_MESSAGES = new Set(["Unexpected token '<'", "expected expression, got '<'"]);
const HASHED_CHUNK_FILE = /\/static\/(js|css)\/[^/]+\.chunk\.(js|css)(\?|#|$)/;

export function isRecoveredStaleChunkParse(event: SentryLikeEvent): boolean {
  if (!event) return false;
  const exception = event.exception?.values?.[0];
  if (exception?.type !== "SyntaxError") return false;
  if (!STALE_CHUNK_PARSE_MESSAGES.has(exception.value || "")) return false;
  const frames = exception.stacktrace?.frames || [];
  const filename = frames[frames.length - 1]?.filename || "";
  return HASHED_CHUNK_FILE.test(filename);
}

// GZ#2998: Safari/iPad "Maximum call stack size exceeded." (#163, #3116-#3119)
// whose frames point at the page URL itself (customers:228) or at no file, at
// lines far past 1. The built index.html is a single line, so code on a later
// line of the page was injected by the browser or an add-on (in-app translate,
// extensions), not shipped by us. Drop only when EVERY frame is like that; a
// stack with any frame in our bundle, another file, or line 1 still reports.
type SentryFrame = { filename?: string; lineno?: number };

function isInjectedPageFrame(frame: SentryFrame): boolean {
  if (!frame.lineno || frame.lineno <= 1) return false;
  const filename = frame.filename || "";
  if (filename === "" || filename === "undefined" || filename === "<anonymous>") return true;
  try {
    const url = new URL(filename);
    const ownPage = url.host === window.location.host || /(^|\.)bookandgo\.app$/.test(url.hostname);
    if (!ownPage) return false;
    // Our bundle and any other real file have an extension; app routes do not.
    return !/\.[a-z0-9]+$/i.test(url.pathname);
  } catch {
    return false;
  }
}

export function isInjectedPageScriptError(event: SentryLikeEvent): boolean {
  if (!event) return false;
  const frames = (event.exception?.values?.[0]?.stacktrace?.frames || []) as SentryFrame[];
  return frames.length > 0 && frames.every(isInjectedPageFrame);
}
